import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Switch, InputNumber, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { TablePaginationConfig, SorterResult, FilterDropdownProps } from 'antd/es/table/interface';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import { taxonomyAPI } from '../../Utils/taxonomyApi';
import { IFilterSettings } from '../../Types/FilterTypes';

interface Category {
  id: string;
  slug: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  isMultiSelect: boolean;
}

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();
  
  // Filter & Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<string>('displayOrder');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  
  const searchInputRef = useRef<InputRef>(null);

  useEffect(() => {
    fetchCategories();
  }, [currentPage, pageSize, filters, sortField, sortOrder]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const filterSettings: IFilterSettings = {
        pagination: {
          page: currentPage,
          take: pageSize,
        },
        sorting: {
          field: sortField,
          order: sortOrder,
        },
      };

      // Add filters
      if (Object.keys(filters).length > 0) {
        filterSettings.filter = Object.entries(filters).map(([field, value]) => ({
          field,
          value,
        }));
      }

      const response = await taxonomyAPI.getAllCategories(filterSettings);
      setCategories(response.data.categories || []);
      setTotalCount(response.data.meta?.count || 0);
    } catch (error) {
      message.error('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: any,
    sorter: SorterResult<Category> | SorterResult<Category>[]
  ) => {
    // Handle pagination
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }

    // Handle sorting
    if (!Array.isArray(sorter) && sorter.field) {
      const field = Array.isArray(sorter.field) ? sorter.field[0] : sorter.field;
      setSortField(String(field));
      setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
    }
  };

  const handleFilter = (field: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[field] = value;
    } else {
      delete newFilters[field];
    }
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter
  };

  const getColumnSearchProps = (dataIndex: string, title: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`${title} durchsuchen`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            handleFilter(dataIndex, selectedKeys[0] as string);
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              handleFilter(dataIndex, selectedKeys[0] as string);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Suchen
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) {
                clearFilters();
              }
              handleFilter(dataIndex, '');
            }}
            size="small"
            style={{ width: 90 }}
          >
            Zurücksetzen
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (category: Category) => {
    navigate(`/categories/${category.id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      await taxonomyAPI.deleteCategory(id);
      message.success('Kategorie erfolgreich gelöscht');
      fetchCategories();
    } catch (error) {
      message.error('Fehler beim Löschen der Kategorie');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCategory) {
        await taxonomyAPI.updateCategory(editingCategory.id, values);
        message.success('Kategorie erfolgreich aktualisiert');
      } else {
        await taxonomyAPI.createCategory(values);
        message.success('Kategorie erfolgreich erstellt');
      }
      setModalVisible(false);
      fetchCategories();
    } catch (error) {
      message.error('Fehler beim Speichern der Kategorie');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      ...getColumnSearchProps('name', 'Name'),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      sorter: true,
      ...getColumnSearchProps('slug', 'Slug'),
    },
    {
      title: 'Reihenfolge',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Mehrfachauswahl',
      dataIndex: 'isMultiSelect',
      key: 'isMultiSelect',
      render: (isMultiSelect: boolean) => (
        <Tag color={isMultiSelect ? 'blue' : 'default'}>
          {isMultiSelect ? 'Ja' : 'Nein'}
        </Tag>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, record: Category) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Bearbeiten
          </Button>
          <Popconfirm
            title="Kategorie löschen?"
            description="Sind Sie sicher, dass Sie diese Kategorie löschen möchten?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Löschen
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Kategorien</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Neue Kategorie
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (total) => `Gesamt: ${total} Kategorien`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      <Modal
        title={editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            isMultiSelect: false,
            displayOrder: 0,
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Bitte Namen eingeben' }]}
          >
            <Input placeholder="z.B. Gehäusematerial" />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug (optional)"
            tooltip="Wird automatisch aus dem Namen generiert, falls leer gelassen"
          >
            <Input placeholder="z.B. case-material (optional)" />
          </Form.Item>

          <Form.Item
            name="displayOrder"
            label="Anzeigereihenfolge"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktiv"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isMultiSelect"
            label="Mehrfachauswahl erlauben"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
